"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import { PageTrademania2Schema } from "@/sanity/pages/PageTrademania2Template";
import { onScrollFrame, prefersReducedMotion } from "@/components/trademania-2/shared";

const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const inOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
// gentle curves for the car: its top speed stays close to the average (cubic peaks at 3x)
const sineOut = (t: number) => Math.sin((t * Math.PI) / 2);
const sineIn = (t: number) => 1 - Math.cos((t * Math.PI) / 2);
const sineInOut = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2;
// nothing is pinned: while the stage crosses the middle of the screen it slows down to this share
// of the scroll speed, then speeds back up. The slow-down is a smooth bump (no corners), sized so
// the stage lags behind by exactly the wrapper's extra height (wrapper 210svh - stage 100svh).
const MIN_SPEED = 0.1;
// phones: rim rotation per px the car moves past the words (its wheels are ~45px across)
const PHONE_SPIN = 2.4;

type Refs = { rear?: React.Ref<HTMLDivElement>; front?: React.Ref<HTMLDivElement> };

/** Car with the two rim layers placed on its wheels (positions in % of the car image). */
function Car({ data, rear, front }: { data: PageTrademania2Schema } & Refs) {
  if (!data?.launchCar?.src) return null;
  return (
    <>
      <Image src={data.launchCar} alt="TradeMania F1" sizes="(max-width: 992px) 125vw, 80vw" className="h-full w-full" />
      {data?.launchWheelRear?.src && (
        <div ref={rear} className="absolute left-[14.493%] top-[48.613%] aspect-square w-[9.207%] will-change-transform">
          <Image src={data.launchWheelRear} alt="" sizes="15vw" className="h-full w-full" />
        </div>
      )}
      {data?.launchWheelFront?.src && (
        <div ref={front} className="absolute left-[77.877%] top-[50.657%] aspect-square w-[8.355%] will-change-transform">
          <Image src={data.launchWheelFront} alt="" sizes="15vw" className="h-full w-full" />
        </div>
      )}
    </>
  );
}

/**
 * Desktop: never pinned; the stage keeps scrolling but slows right down while it crosses the
 * middle of the screen. Meanwhile the car drives in, the giant words run along with it until the
 * last one is centred, and the car drives out as the stage speeds up again. Rims rotate by the
 * distance travelled.
 * Phones: the Figma frame (12818:174592, 430 × 251) is the state with the stage centred on
 * screen: the word row centred, the car parked in front of it. While the stage crosses the
 * screen the row runs from its first word to its last and the car drives in and out again, the
 * rims turning with it (backwards when scrolling back up).
 */
export default function Launch({ data }: { data: PageTrademania2Schema }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const carRef = useRef<HTMLDivElement>(null);
  const rearRef = useRef<HTMLDivElement>(null);
  const frontRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const phoneRowRef = useRef<HTMLDivElement>(null);
  const phoneCarRef = useRef<HTMLDivElement>(null);
  const phoneRearRef = useRef<HTMLDivElement>(null);
  const phoneFrontRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const stage = stageRef.current;
    const row = rowRef.current;
    const car = carRef.current;
    const phone = phoneRef.current;
    const phoneRow = phoneRowRef.current;
    const phoneCar = phoneCarRef.current;
    if (!section || !stage || !row || !car || !phone || !phoneRow) return;

    if (prefersReducedMotion()) {
      const last = row.lastElementChild as HTMLElement | null;
      if (last) row.style.transform = `translate3d(${window.innerWidth / 2 - (last.offsetLeft + last.offsetWidth / 2)}px,0,0)`;
      car.style.transform = "translate(-50%, -18%)";
      return;
    }

    let smoothTop = section.getBoundingClientRect().top;
    let lastFrame = performance.now();
    let phoneDist = 0;
    let phoneLast: number | null = null;

    const updatePhone = () => {
      const r = phone.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      if (r.bottom < 0 || r.top > vh) return;
      // 0 when the stage enters at the bottom, 1 once it has left at the top; .5 = centred (Figma)
      const p = clamp((vh - r.top) / (vh + r.height));
      // the stage is fully on screen from `full` to `1 - full`: the row starts there with its first
      // word at the 16px margin and ends with its last word at the opposite margin
      const full = Math.min(0.45, r.height / (vh + r.height));
      const reach = Math.max(0, (phoneRow.offsetWidth - vw * (1 - 32 / 430)) / 2);
      const tx = (reach * (0.5 - p)) / (0.5 - full);
      phoneRow.style.transform = `translate3d(${tx.toFixed(2)}px,0,0)`;

      // the car parks by 35% (just after the stage is fully in) and leaves from 65%
      const cx = (-1.15 + 1.15 * sineOut(clamp(p / 0.35)) + 1.15 * sineInOut(clamp((p - 0.65) / 0.35))) * vw;
      if (phoneCar) phoneCar.style.transform = `translate(calc(-50% + ${cx.toFixed(2)}px),0)`;
      // the car moves right past the words when the row moves left
      const travel = cx - tx;
      if (phoneLast !== null) phoneDist += travel - phoneLast;
      phoneLast = travel;
      const spin = `rotate(${(phoneDist * PHONE_SPIN).toFixed(1)}deg)`;
      if (phoneRearRef.current) phoneRearRef.current.style.transform = spin;
      if (phoneFrontRef.current) phoneFrontRef.current.style.transform = spin;
    };

    const update = () => {
      if (window.innerWidth < 993) return updatePhone();
      // slow zone: the stage's own offset inside the taller wrapper grows along a smoothstep
      // whose slope is a raised cosine, so its speed on screen eases 1 -> MIN_SPEED -> 1. Centred
      // on the moment the stage fills the screen (stage top at 0).
      const r = section.getBoundingClientRect();
      const now = performance.now();
      const dt = Math.min(64, now - lastFrame);
      lastFrame = now;
      if (r.bottom < 0 || r.top > window.innerHeight) smoothTop = r.top;
      else smoothTop += (r.top - smoothTop) * (1 - Math.exp(-dt / 100));
      const extra = Math.max(1, r.height - stage.offsetHeight);
      const span = (2 * extra) / (1 - MIN_SPEED);
      const x = clamp((span / 2 - extra / 2 - smoothTop) / span);
      const offset = extra * (x - Math.sin(2 * Math.PI * x) / (2 * Math.PI));
      stage.style.transform = `translate3d(0,${(offset + smoothTop - r.top).toFixed(2)}px,0)`;
      const p = x;
      const vw = window.innerWidth;
      const last = row.lastElementChild as HTMLElement | null;
      const endX = last ? vw / 2 - (last.offsetLeft + last.offsetWidth / 2) : 0;
      // the first word starts left of centre, so the copy is already on screen as it comes in
      const startX = vw * 0.38;
      // the last word is centred just before the stage starts to speed up again
      const tx = startX + (endX - startX) * inOut(clamp((p - 0.05) / 0.67));
      row.style.transform = `translate3d(${tx}px,0,0)`;

      // in over the first 30% of the slow zone, out over the last 30% while the stage speeds up
      // again. The distances end just past the screen edge, so no travel is wasted off-screen. The
      // exit mirrors the entry: it pulls away as slowly as it parked and is fastest once off-screen
      const cx = (-0.9 + 0.9 * sineOut(clamp(p / 0.3)) + 0.9 * sineIn(clamp((p - 0.7) / 0.3))) * vw;
      car.style.transform = `translate(calc(-50% + ${cx}px), -18%)`;
      const spin = `rotate(${(cx - tx) * 0.9}deg)`;
      if (rearRef.current) rearRef.current.style.transform = spin;
      if (frontRef.current) frontRef.current.style.transform = spin;
    };
    update();
    return onScrollFrame(update);
  }, []);

  const words = data?.launchMarquee ?? [];
  const aspectRatio = data?.launchCar ? `${data.launchCar.width} / ${data.launchCar.height}` : undefined;

  return (
    <section className="relative">
      {/* phones (Figma 12818:174592): the desktop words in Mona Sans Bold 120px, trimmed to the cap
          height with their top at 64px and centred (an overflowing flex child overflows both
          sides). The car spans 497 × 145 from 88px down, 1.25px left of the middle, which puts its
          body exactly where the Figma cutout has it */}
      <div ref={phoneRef} className="relative h-[31.375rem] overflow-hidden pt-[8rem] lg:hidden">
        <div className="flex justify-center">
          <div
            ref={phoneRowRef}
            className="-mt-[.1585em] flex w-max shrink-0 gap-[.1795em] whitespace-nowrap font-mona text-[15rem] font-bold uppercase leading-none tracking-[-.0561em] text-black will-change-transform"
          >
            {words.map((word, i) => (
              <span key={i}>{word}</span>
            ))}
          </div>
        </div>
        <div
          ref={phoneCarRef}
          className="absolute left-[calc(50%-.156rem)] top-[11.01rem] w-[62.16rem] will-change-transform"
          style={{ aspectRatio, transform: "translate(-50%,0)" }}
        >
          <Car data={data} rear={phoneRearRef} front={phoneFrontRef} />
        </div>
      </div>

      {/* desktop */}
      <div ref={sectionRef} className="relative hidden h-[210svh] motion-reduce:h-[100svh] lg:block">
        <div ref={stageRef} className="absolute inset-x-0 top-0 flex h-[100svh] items-center overflow-hidden will-change-transform">
          <div className="absolute inset-x-0 top-1/2 -translate-y-[62%]">
            <div
              ref={rowRef}
              className="flex w-max gap-[6rem] whitespace-nowrap font-mona text-[37.5rem] font-bold uppercase leading-[.73] tracking-[-.05em] text-black will-change-transform"
            >
              {words.map((word, i) => (
                <span key={i}>{word}</span>
              ))}
            </div>
          </div>

          <div
            ref={carRef}
            className="absolute left-1/2 top-1/2 w-[146.625rem] will-change-transform"
            style={{ aspectRatio, transform: "translate(-160%, -18%)" }}
          >
            <Car data={data} rear={rearRef} front={frontRef} />
          </div>
        </div>
      </div>
    </section>
  );
}
